/**
 * 来場者向け利用規約モーダル
 * 新規登録前に現在有効な規約全文を確認できるようにします
 */

import React from 'react';
import { Modal, Pressable, ScrollView, Text, View } from '../../../shared/components/WebPrimitives';
import { VISITOR_TERMS_VERSION_DISPLAY_LABEL } from '../constants';

/** 利用規約表示データ */
type VisitorTerms = {
  version: string;
  title: string;
  content: string;
  effectiveAt: string;
};

/** 利用規約モーダルのプロパティ */
type VisitorTermsModalProps = {
  visible: boolean;
  terms: VisitorTerms | null;
  mode?: 'viewer' | 'reconsent';
  onClose?: () => void;
  isTermsAccepted?: boolean;
  isLoading?: boolean;
  isSubmitting?: boolean;
  errorMessage?: string;
  onToggleTermsAccepted?: () => void;
  onAccept?: () => Promise<void>;
  onLogout?: () => void;
  onRetry?: () => void;
};

/**
 * 利用規約の施行日時を日本時間の日付へ整形する
 * @param {string} effectiveAt - 施行日時
 * @returns {string} 表示用日付
 */
export const formatTermsEffectiveDate = (effectiveAt) => {
  /** 施行日時 */
  const effectiveDate = new Date(effectiveAt);

  if (Number.isNaN(effectiveDate.getTime())) {
    return effectiveAt || '未設定';
  }

  return new Intl.DateTimeFormat('ja-JP', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: 'Asia/Tokyo',
  }).format(effectiveDate);
};

/** 見出しとして扱うブロック種別（アクセシビリティroleの付与判定に使う） */
const TERMS_HEADING_BLOCK_TYPES = ['heading1', 'heading2', 'heading3'];

/** ブロック種別ごとの見た目クラス（見出しは段階的に強調し、本文は改行を保持する） */
const TERMS_BLOCK_CLASS_NAME_MAP = Object.freeze({
  heading1: 'whitespace-pre-wrap text-lg font-bold leading-7 text-app-quest-night',
  heading2: 'whitespace-pre-wrap text-base font-bold leading-6 text-app-quest-night',
  heading3: 'whitespace-pre-wrap text-sm font-bold leading-6 text-app-quest-night',
  paragraph: 'whitespace-pre-wrap text-sm leading-7 text-app-text',
});

/** 見出し記法の行パターン（#=1〜###=3個をレベルとして捉え、見出しテキストは省略可） */
const TERMS_HEADING_LINE_PATTERN = /^(#{1,3})(?:\s+(.*))?$/;

/** 見出しレベル（#の個数）とブロック種別の対応表 */
const TERMS_HEADING_LEVEL_TYPE_MAP = Object.freeze({
  1: 'heading1',
  2: 'heading2',
  3: 'heading3',
});

/**
 * 利用規約本文の行を、見出し（#/##/###）と段落のブロック配列へ分解する
 * 規約本文はMarkdown相当の見出し記法（#=大見出し、##=中見出し、###=小見出し）を含むため、
 * プレーンテキストのまま表示すると記号がそのまま見えてしまう。行単位で見出しを判定し、
 * 見出し以外の連続行は空行区切りで1つの段落としてまとめる（段落内の改行はそのまま保持する）。
 * @param {string} content - 利用規約本文（Markdown相当の見出し記法を含む）
 * @returns {Array<{ type: 'heading1' | 'heading2' | 'heading3' | 'paragraph', text: string }>} 表示用ブロック配列
 */
export const parseTermsContentBlocks = (content) => {
  if (typeof content !== 'string' || content.trim() === '') {
    return [];
  }

  /** 行配列（CRLF/LFどちらの改行コードにも対応する） */
  const lines = content.split(/\r\n|\n/);

  /** 生成済みブロック */
  const blocks = [];

  /** 段落として溜めている行 */
  let paragraphLines = [];

  /**
   * 溜めていた段落行をひとつの段落ブロックとして確定する
   * @returns {void}
   */
  const flushParagraph = () => {
    /** 前後の空行を取り除いた段落本文 */
    const paragraphText = paragraphLines.join('\n').trim();

    if (paragraphText !== '') {
      blocks.push({ type: 'paragraph', text: paragraphText });
    }

    paragraphLines = [];
  };

  lines.forEach((line) => {
    /** 見出し判定用に前後の空白を除いた行 */
    const trimmedLine = line.trim();

    /** 見出し記法へのマッチ結果（見出しでなければnull） */
    const headingMatch = trimmedLine.match(TERMS_HEADING_LINE_PATTERN);

    if (headingMatch) {
      flushParagraph();
      /** 見出しレベル（#の個数） */
      const headingLevel = headingMatch[1].length;
      blocks.push({
        type: TERMS_HEADING_LEVEL_TYPE_MAP[headingLevel],
        text: (headingMatch[2] ?? '').trim(),
      });
      return;
    }

    if (trimmedLine === '') {
      // 空行は段落の区切りとして扱う
      flushParagraph();
      return;
    }

    paragraphLines.push(line);
  });

  flushParagraph();

  return blocks;
};

/**
 * 来場者向け利用規約モーダル
 * @param {Object} props - プロパティ
 * @param {boolean} props.visible - 表示状態
 * @param {{ version: string, title: string, content: string, effectiveAt: string } | null} props.terms - 利用規約
 * @param {'viewer' | 'reconsent'} [props.mode='viewer'] - 表示用途
 * @param {() => void} [props.onClose] - 閉じる処理
 * @param {boolean} [props.isTermsAccepted=false] - 再同意チェック状態
 * @param {boolean} [props.isLoading=false] - 再同意要否の確認中状態
 * @param {boolean} [props.isSubmitting=false] - 再同意送信中状態
 * @param {string} [props.errorMessage=''] - 再同意処理エラー
 * @param {() => void} [props.onToggleTermsAccepted] - 再同意チェック切替処理
 * @param {() => Promise<void>} [props.onAccept] - 再同意処理
 * @param {() => void} [props.onLogout] - ログアウト処理
 * @param {() => void} [props.onRetry] - 同意状況の取得失敗時に確認をやり直す処理
 * @returns {JSX.Element | null} 利用規約モーダル
 */
const VisitorTermsModal = ({
  visible,
  terms,
  mode = 'viewer',
  onClose,
  isTermsAccepted = false,
  isLoading = false,
  isSubmitting = false,
  errorMessage = '',
  onToggleTermsAccepted,
  onAccept,
  onLogout,
  onRetry,
}: VisitorTermsModalProps) => {
  /** 既存来場者の再同意表示か */
  const isReconsent = mode === 'reconsent';
  /** 再同意チェックを操作できない状態か */
  const isConsentCheckboxDisabled = Boolean(
    !terms || isLoading || isSubmitting,
  );
  /** 再同意を送信できない状態か */
  const isAcceptDisabled = Boolean(
    isConsentCheckboxDisabled || !isTermsAccepted || !onAccept,
  );
  /** モーダルの一意なアクセシブル名 */
  const modalAccessibilityLabel = isReconsent
    ? `利用規約への再同意${terms?.title ? `: ${terms.title}` : ''}`
    : `利用規約: ${terms?.title ?? ''}`;

  if (!isReconsent && !terms) {
    return null;
  }

  return (
    <Modal
      accessibilityLabel={modalAccessibilityLabel}
      animationType="fade"
      dismissible={!isReconsent}
      onRequestClose={onClose}
      statusBarTranslucent
      transparent
      visible={visible}
    >
      <View className="min-h-0 flex-1 justify-center overflow-hidden bg-black/60 px-4 py-8">
        <View
          className="flex max-h-full min-h-0 w-full max-w-3xl flex-col self-center overflow-hidden border-2 border-app-quest-night bg-[#FFF8DD] p-4"
          testID="visitor-terms-dialog-panel"
        >
          <View className="shrink-0 border-b-2 border-app-quest-night/20 pb-3">
            <Text className="text-xs font-bold tracking-widest text-app-text-secondary">
              {isReconsent ? 'TERMS RECONSENT' : 'TERMS OF USE'}
            </Text>
            <Text className="mt-1 text-lg font-bold text-app-quest-night">
              {terms?.title ?? '利用規約の再同意'}
            </Text>
            {terms ? (
              <Text className="mt-1 text-xs text-app-text-secondary">
                版: {VISITOR_TERMS_VERSION_DISPLAY_LABEL} / 更新日: {formatTermsEffectiveDate(terms.effectiveAt)}
              </Text>
            ) : null}
          </View>

          {isLoading ? (
            <Text
              accessibilityLiveRegion="polite"
              accessibilityRole="status"
              className="mt-4 text-sm font-semibold text-app-text"
            >
              利用規約の同意状況を確認しています...
            </Text>
          ) : null}

          {errorMessage ? (
            <Text
              accessibilityLiveRegion="assertive"
              accessibilityRole="alert"
              className="mt-4 text-sm font-semibold leading-6 text-app-danger"
            >
              {errorMessage}
            </Text>
          ) : null}

          {terms ? (
            <ScrollView
              className="mt-3 min-h-0 flex-1"
              contentContainerClassName="gap-3 pb-6"
              showsVerticalScrollIndicator
              testID="visitor-terms-content-scroll-view"
            >
              {parseTermsContentBlocks(terms.content).map((block, index) => (
                <Text
                  accessibilityRole={TERMS_HEADING_BLOCK_TYPES.includes(block.type) ? 'header' : undefined}
                  className={TERMS_BLOCK_CLASS_NAME_MAP[block.type] ?? TERMS_BLOCK_CLASS_NAME_MAP.paragraph}
                  key={`terms-content-block-${index}`}
                >
                  {block.text}
                </Text>
              ))}
            </ScrollView>
          ) : null}

          {isReconsent ? (
            <View className="mt-3 shrink-0 gap-3 border-t-2 border-app-quest-night/20 pt-3">
              <Text className="text-xs leading-5 text-app-text-secondary">
                同意しない場合は本アプリを利用できません。利用を終了する場合はログアウトしてください。
              </Text>

              {onRetry && !isLoading ? (
                <Pressable
                  accessibilityRole="button"
                  className="min-h-[48px] justify-center border-2 border-app-quest-night bg-white px-3"
                  onPress={onRetry}
                >
                  <Text className="text-center text-sm font-bold text-app-quest-night">もう一度確認する</Text>
                </Pressable>
              ) : null}

              <Pressable
                accessibilityLabel="利用規約に同意します"
                accessibilityRole="checkbox"
                accessibilityState={{
                  checked: isTermsAccepted,
                  disabled: isConsentCheckboxDisabled,
                }}
                aria-checked={isTermsAccepted}
                className={`min-h-[48px] flex-row items-center gap-3 border-2 px-3 py-2 ${
                  isConsentCheckboxDisabled
                    ? 'border-app-outline bg-app-surface-variant'
                    : 'border-app-quest-night/30 bg-[#FFFDF0]'
                }`}
                disabled={isConsentCheckboxDisabled}
                onPress={onToggleTermsAccepted}
                testID="visitor-terms-reconsent-checkbox"
              >
                <View
                  className={`h-6 w-6 items-center justify-center border-2 ${
                    isConsentCheckboxDisabled ? 'border-app-outline' : 'border-app-quest-night'
                  } ${isTermsAccepted ? 'bg-app-quest-night' : 'bg-white'}`}
                >
                  <Text className="text-sm font-bold text-white">{isTermsAccepted ? '✓' : ''}</Text>
                </View>
                <Text
                  className={`flex-1 text-sm font-semibold ${
                    isConsentCheckboxDisabled ? 'text-app-outline' : 'text-app-text'
                  }`}
                >
                  利用規約に同意します
                </Text>
              </Pressable>

              <View className="flex-row gap-2">
                <Pressable
                  accessibilityRole="button"
                  className="min-h-[48px] flex-1 justify-center border-2 border-app-danger bg-white px-3"
                  disabled={isSubmitting}
                  onPress={onLogout}
                >
                  <Text className="text-center text-sm font-bold text-app-danger">ログアウト</Text>
                </Pressable>
                <Pressable
                  accessibilityRole="button"
                  className={`min-h-[48px] flex-1 justify-center border-2 px-3 ${
                    isAcceptDisabled
                      ? 'border-app-outline bg-app-surface-variant'
                      : 'border-app-quest-night bg-app-quest-night'
                  }`}
                  disabled={isAcceptDisabled}
                  onPress={onAccept}
                >
                  <Text
                    className={`text-center text-sm font-bold ${
                      isAcceptDisabled ? 'text-app-outline' : 'text-white'
                    }`}
                  >
                    {isSubmitting ? '同意しています...' : '同意する'}
                  </Text>
                </Pressable>
              </View>
            </View>
          ) : (
            <Pressable
              accessibilityLabel="利用規約を閉じる"
              accessibilityRole="button"
              className="mt-3 min-h-[48px] shrink-0 justify-center border-2 border-app-quest-night bg-app-quest-night px-4"
              onPress={onClose}
            >
              <Text className="text-center text-sm font-bold text-white">閉じる</Text>
            </Pressable>
          )}
        </View>
      </View>
    </Modal>
  );
};

export default VisitorTermsModal;
